import { DragEvent, ReactNode, useState } from 'react';

type Identified = { id: string };

interface Props<T extends Identified> {
  items: T[];
  onReorder: (ids: string[]) => void;
  renderItem: (item: T, handlers: { dragHandleProps: DragHandleProps; state: DragState }) => ReactNode;
  /** Stable key for container — used to refuse cross-list drops. */
  groupKey: string;
}

export interface DragHandleProps {
  draggable: true;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}

export type DragState = 'resting' | 'dragging' | 'drop-target' | 'ghost';

/**
 * HTML5-native sortable list. Desktop-only (native drag events don't fire on
 * touch). Each row renders with a drag handle that emits onDragStart; the row
 * below the cursor gets a drop-target marker. Drop commits a new order and
 * calls onReorder with the new id sequence.
 */
export function SortableList<T extends Identified>({ items, onReorder, renderItem, groupKey }: Props<T>) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const handleDragStart = (id: string) => (e: DragEvent) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/x-ts-group', groupKey);
    e.dataTransfer.setData('text/x-ts-id', id);
  };

  const handleDragEnd = () => { setDraggingId(null); setOverId(null); };

  const handleDragOver = (id: string) => (e: DragEvent) => {
    if (draggingId && id !== draggingId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setOverId(id);
    }
  };

  const handleDrop = (targetId: string) => (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.getData('text/x-ts-group') !== groupKey) return;
    const sourceId = e.dataTransfer.getData('text/x-ts-id') || draggingId;
    if (!sourceId || sourceId === targetId) { handleDragEnd(); return; }

    const ids = items.map((i) => i.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { handleDragEnd(); return; }
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);
    handleDragEnd();
    onReorder(ids);
  };

  return (
    <>
      {items.map((item) => {
        const state: DragState =
          draggingId === item.id ? 'dragging'
            : overId === item.id ? 'drop-target'
              : 'resting';
        return (
          <div
            key={item.id}
            onDragOver={handleDragOver(item.id)}
            onDrop={handleDrop(item.id)}
            onDragLeave={() => { if (overId === item.id) setOverId(null); }}
          >
            {renderItem(item, {
              dragHandleProps: {
                draggable: true,
                onDragStart: handleDragStart(item.id),
                onDragEnd: handleDragEnd,
              },
              state,
            })}
          </div>
        );
      })}
    </>
  );
}
